import { Modal as BaseModal } from '@mui/material';
import clsx from 'clsx';
import React from 'react';

import { TextIconColor } from '../constants';
import { Icon } from '../Icon';
import { IconButton, IconButtonType } from '../IconButton';

import { modalStyles, ModalWidth } from './Modal.styles';

interface ModalProps {
  children: React.ReactNode;
  open: boolean;
  noContentPadding?: boolean;
  onClose?: () => void;
  overflowAuto?: boolean;
  width?: ModalWidth;
}

// Backdrop component for MUI Modal
// https://mui.com/base-ui/react-modal/
const Backdrop = React.forwardRef<
  HTMLDivElement,
  { className: string; open?: boolean }
>((props, ref) => {
  const { open, className, ...other } = props;
  return (
    <div
      className={clsx({ 'MuiBackdrop-open': open }, className)}
      ref={ref}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...other}
    />
  );
});

// Styled backdrop with fixed styles like the original twin.macro version
const StyledBackdrop = React.forwardRef<
  HTMLDivElement,
  { className?: string; open?: boolean }
>((props, ref) => (
  <Backdrop
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
    className={clsx(
      'fixed inset-0 z-[-1] bg-black bg-opacity-60 transition-opacity',
      props.className
    )}
    ref={ref}
  />
));

// Create a styled modal with fixed base styles
const StyledModal = ({
  className,
  ...props
}: React.ComponentProps<typeof BaseModal>) => (
  <BaseModal
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
    className={clsx(
      'fixed inset-0 z-50 m-1 flex items-center justify-center overflow-auto',
      className
    )}
  />
);

export const Modal = ({
  open,
  onClose,
  children,
  overflowAuto = true,
  width = ModalWidth.LG,
  noContentPadding = false
}: ModalProps) => {
  // Early return if modal is not open
  if (!open) return null;

  // Get just the content and closeButton styles
  const styles = modalStyles({
    width,
    noContentPadding,
    overflowAuto,
    hasCloseButton: !!onClose
  });

  const handleClose = () => {
    if (!onClose) return;
    onClose();
  };

  // https://mui.com/base-ui/react-modal/
  return (
    <StyledModal
      aria-modal="true"
      onClose={handleClose}
      open={open}
      slots={{
        backdrop: StyledBackdrop
      }}
    >
      <div className={styles.content()}>
        {!!onClose && (
          <div className={styles.closeButton()}>
            <IconButton
              color={TextIconColor.PRIMARY}
              icon={Icon.CLOSE}
              onClick={handleClose}
              type={IconButtonType.SIMPLE}
            />
          </div>
        )}
        {children}
      </div>
    </StyledModal>
  );
};

export default Modal;
